import { HttpClient } from '@angular/common/http';
import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, firstValueFrom, Observable, throwError } from 'rxjs';
import { AnonymousLegalDocuments } from 'projects/borrower-app/src/app/models/anonymous-legal-documents.model';
import { GlobalConfigService } from 'projects/borrower-app/src/app/services/global-config.service';
import { isBlankHtml, LegalDocument, pickCustomLegalHtml } from './legal-documents';

const STATIC_ASSET: Record<LegalDocument, string> = {
  termsOfUse: './assets/terms-of-use.html',
  privacyPolicy: './assets/privacy-statement.html',
};

/** The lender's documents could not be loaded; callers show an unavailable/retry state, never the default. */
export class LegalDocumentsUnavailableError extends Error {
  constructor() {
    super('Legal documents are unavailable.');
    this.name = 'LegalDocumentsUnavailableError';
  }
}

export interface LegalDocumentsHtml {
  termsOfUse: string;
  privacyPolicy: string;
}

/**
 * Resolves the Terms of Use / Privacy Policy HTML shown to borrowers (LLS-886 §4): the lender's
 * document for the active language (Spanish → English), otherwise the static Lodasoft default.
 * Lender HTML is sanitized because it can be rendered into an iframe or bypass-trusted markup.
 * The static default is only for a successful "nothing configured" answer: a failed lookup rejects with
 * LegalDocumentsUnavailableError instead of silently showing different legal content.
 */
@Injectable({ providedIn: 'root' })
export class LegalDocumentsService {
  constructor(
    private readonly _http: HttpClient,
    private readonly _globalConfig: GlobalConfigService,
    private readonly _transloco: TranslocoService,
    private readonly _sanitizer: DomSanitizer,
    private readonly _window: Window,
  ) {}

  /** Both documents (Terms page): one language for both, captured when the load starts (the page reloads on a switch). */
  async getDocuments(companyGuid: string | null | undefined): Promise<LegalDocumentsHtml> {
    const lang = this._transloco.getActiveLang();
    const docs = await this.fetch(companyGuid);
    const [termsOfUse, privacyPolicy] = await Promise.all([
      this.resolve('termsOfUse', docs, lang),
      this.resolve('privacyPolicy', docs, lang),
    ]);
    return { termsOfUse, privacyPolicy };
  }

  /**
   * One document (account dialog). The language is read after every load step (lender request and the static
   * default), and the document is re-resolved if it changed meanwhile, so the dialog matches the active language.
   */
  async getHtml(doc: LegalDocument, companyGuid: string | null | undefined): Promise<string> {
    const docs = await this.fetch(companyGuid);
    let lang = this._transloco.getActiveLang();
    let html = await this.resolve(doc, docs, lang);
    for (let attempt = 0; attempt < 3 && lang !== this._transloco.getActiveLang(); attempt++) {
      lang = this._transloco.getActiveLang();
      html = await this.resolve(doc, docs, lang);
    }
    return html;
  }

  private async resolve(doc: LegalDocument, docs: AnonymousLegalDocuments | null, lang: string): Promise<string> {
    const custom = pickCustomLegalHtml(docs, doc, lang);
    if (custom) {
      const safe = this._sanitizer.sanitize(SecurityContext.HTML, custom) ?? '';
      if (!isBlankHtml(safe)) {
        return safe;
      }
    }
    try {
      const html = await firstValueFrom(this._http.get(STATIC_ASSET[doc], { responseType: 'text' }));
      return doc === 'termsOfUse' ? html.replace('[URL]', this._window.location.hostname) : html;
    } catch {
      return '';
    }
  }

  private fetch(companyGuid: string | null | undefined): Promise<AnonymousLegalDocuments | null> {
    const request: Observable<AnonymousLegalDocuments> = companyGuid
      ? this._globalConfig.getAnonymousLegalDocuments(companyGuid)
      : this._globalConfig.getAnonymousLegalDocumentsByReferrer();
    return firstValueFrom(request.pipe(catchError(() => throwError(() => new LegalDocumentsUnavailableError()))));
  }
}
